import React from "react";
import {
  Box,
  CircularProgress,
  Typography,
  Skeleton,
  Card,
  CardContent,
  Grid,
} from "@mui/material";

interface OptimizedLoaderProps {
  type?: "fullscreen" | "inline" | "skeleton" | "table";
  message?: string;
  size?: "small" | "medium" | "large";
  rows?: number;
}

const OptimizedLoader: React.FC<OptimizedLoaderProps> = ({
  type = "fullscreen",
  message = "Loading...",
  size = "medium",
  rows = 5,
}) => {
  const getSize = () => {
    switch (size) {
      case "small":
        return 30;
      case "large":
        return 60;
      default:
        return 40;
    }
  };

  if (type === "skeleton") {
    return (
      <Box sx={{ width: "100%" }}>
        {/* Summary Cards Skeleton */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={12} md={3} key={item}>
              <Card sx={{ height: "100%", boxShadow: 2 }}>
                <CardContent>
                  <Skeleton variant="text" width="60%" height={20} />
                  <Skeleton variant="text" width="40%" height={32} sx={{ mt: 1 }} />
                  <Skeleton variant="text" width="80%" height={16} sx={{ mt: 1 }} />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Table Skeleton */}
        <Card sx={{ boxShadow: 2 }}>
          <CardContent>
            <Skeleton variant="rectangular" width="100%" height={400} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (type === "table") {
    return (
      <Box sx={{ width: "100%", p: 2 }}>
        {Array.from({ length: rows }).map((_, index) => (
          <Box key={index} sx={{ mb: 2 }}>
            <Skeleton variant="rectangular" width="100%" height={60} />
          </Box>
        ))}
      </Box>
    );
  }

  if (type === "inline") {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          p: 2,
        }}
      >
        <CircularProgress size={getSize()} />
        <Typography variant="body2" color="textSecondary">
          {message}
        </Typography>
      </Box>
    );
  }

  // Fullscreen loader (default)
  return (
    <>
      {/* Fullscreen Overlay */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backdropFilter: "blur(1.5px)",
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          zIndex: 9999,
        }}
      />
      {/* Loader Container */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 10000,
        }}
      >
        <CircularProgress size={getSize()} />
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ mt: 2, textAlign: "center" }}
        >
          {message}
        </Typography>
      </Box>
    </>
  );
};

export default OptimizedLoader;